import React, { useState } from "react";
import { Box, Button, Container, Grid, Typography } from "@mui/material";

// Importing Custom SVG Icons
import ContentContainer from "./ContentContainer";
import CardPaymentIframe from "../components/PaymentComponent";
import useProtectedStep from "../hooks/useProtectedStep";
import { Trans, useTranslation } from "react-i18next";
import { useBrandAppearance } from "../hooks/useBrandAppearance";


const PaymentPage: React.FC = () => {
    useProtectedStep();
    const { colors: BrandColors } = useBrandAppearance();
    const [showIframe, setShowIframe] = useState(false);

    const { t } = useTranslation("payment");

    return (
        <ContentContainer
        >
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                maxWidth: "1200px",
                margin: "0 auto",
            }}>
                {/* Title Section */}
                <Container maxWidth="lg"
                    sx={{
                        mt: 2,
                        textAlign: "left",
                        display: "flex",
                        position: "relative",
                    }}>
                </Container>

                {/* Content */}
                <Container maxWidth="lg">
                    {/* Title and Paragraph aligned to top and centered horizontally */}
                    <Box
                        sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            alignItems: "center",
                            textAlign: "center",
                            paddingX: 0,
                        }}
                    >
                        {/* Title */}
                        <Typography
                            variant="h4"
                            sx={{
                                fontFamily: "'Dala Floda', serif",
                                fontSize: "2.5rem",
                                fontWeight: "bold",
                                mb: 4,
                            }}
                        >
                            {t('title')}
                        </Typography>


                        <Grid
                            container
                            spacing={{ xs: 2, md: 22 }}
                            sx={{ width: "100%", textAlign: "left", p: 0 }}
                        >
                            {/* Left Column (Room Summary + Info Text) */}
                            <Grid item xs={12} md={6}>
                                {/* Desktop full breakdown */}
                                <Box sx={{ display: { xs: "none", md: "block" } }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: "bold", color: "#7C624B", mb: 2 }}
                                    >
                                        {t('priceSummary')}
                                    </Typography>

                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="body1">Room 1 cost</Typography>
                                        </Grid>
                                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                                            <Typography variant="body1">€ 483,00</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body1" sx={{ fontWeight: "bold" }}>{t('subtotal')}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                                            <Typography variant="body1" sx={{ fontWeight: "bold" }}>€ 483,00</Typography>
                                        </Grid>
                                        <Grid item xs={6} sx={{ marginTop: 4 }}>
                                            <Typography variant="body1">{t('estimatedTaxes')}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sx={{ textAlign: "right", marginTop: 4 }}>
                                            <Typography variant="body1">€ 0,00</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="body1">{t('estimatedAdditionalFees')}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                                            <Typography variant="body1">€ 0,00</Typography>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Box sx={{ height: "2px", backgroundColor: "#C4B59D", mx: "2px", my: "2rem" }} />
                                        </Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="body1" sx={{ fontWeight: "bold", mt: 3 }}>{t('totalPrice')}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sx={{ textAlign: "right" }}>
                                            <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "2.5rem" }}>€ 483,00</Typography>
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Mobile simplified summary */}
                                <Box sx={{ display: { xs: "block", md: "none" }, textAlign: "center", mb: 2 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: "bold", color: "#7C624B", mb: 1 }}
                                    >
                                        {t('priceSummary')}
                                    </Typography>
                                    <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                                        € 483,00
                                    </Typography>
                                </Box>

                                {/* Info Text - Always visible */}
                                <Box mt={2}>
                                    <Typography variant="body1">{t('processingTime')}</Typography>
                                    <Typography variant="body1">{t('fee')}</Typography>
                                    <Typography variant="body1">
                                        <Trans
                                            i18nKey="invoiceNote"
                                            ns="payment"
                                            components={{ strong: <strong /> }}
                                        />
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: "bold", mt: 2 }}>
                                        {t('additionalServices')}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Right Column (Card Payment) */}
                            <Grid item xs={12} md={6}>
                                {!showIframe ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: "100%",
                                            textAlign: "center",
                                        }}
                                    >
                                        <Typography sx={{ mb: 2 }}>
                                            {t('skipPayment')}
                                        </Typography>
                                        <Typography sx={{ mb: 2 }}>{t('or')}</Typography>
                                        <Button
                                            variant="contained"
                                            onClick={() => setShowIframe(true)}
                                            sx={{
                                                width: "100%",
                                                backgroundColor: BrandColors.accent,
                                                color: "#fff",
                                                px: 4,
                                                py: 2,
                                                borderRadius: 0,
                                                fontWeight: "bold",
                                                "&:hover": {
                                                    backgroundColor: BrandColors.accent,
                                                },
                                            }}
                                        >
                                            {t('payNow')}
                                        </Button>
                                    </Box>
                                ) : (
                                    <CardPaymentIframe onClose={() => setShowIframe(false)} />
                                )}
                            </Grid>
                        </Grid>


                    </Box>
                </Container>
            </Box>
        </ContentContainer>
    );
};

export default PaymentPage;